import { create } from 'zustand';

const ACTIVE_ORG_STORAGE_KEY = 'kyra_active_organization_id';

export function getStoredActiveOrgId() {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
  const parsed = raw ? Number(raw) : NaN;
  return Number.isFinite(parsed) ? parsed : null;
}

export function setStoredActiveOrgId(orgId: number | null) {
  if (typeof window === 'undefined') return;
  if (orgId && Number.isFinite(orgId)) {
    window.localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, String(orgId));
  } else {
    window.localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
  }
}

export type OrgInfo = {
  id: number;
  nombre: string;
  logo_url?: string | null;
  moneda_locale?: string | null;
  moneda_formato?: {
    symbol: string;
    symbol_position: 'prefix' | 'suffix';
    symbol_spacing: boolean;
    thousands_sep: string;
    decimal_sep: string;
    decimals: number;
  } | null;
  moneda_base?: {
    codigo_iso: string;
    simbolo?: string | null;
  } | null;
  role: 'admin' | 'editor' | 'viewer';
  is_owner?: boolean;
  status: string;
};

export type TokenClaims = {
  org_id?: number;
  role?: string;
  scopes?: string[];
  superadmin?: boolean;
};

export type LicenseState = {
  max_usuarios: number | null;
  active_users: number;
  pending_invitations: number;
  seats_available: number | null;
  operational: boolean;
};

export type ModuleAccess = {
  moduleKey: string;
  role: 'viewer' | 'operator' | 'supervisor';
  capabilities: string[];
};

export type PublicSiteAccess = {
  role: 'superadmin' | 'editor' | 'publisher' | null;
  can_read: boolean;
  can_write: boolean;
  can_publish: boolean;
  can_media: boolean;
  can_manage_editors: boolean;
};

type TenantState = {
  organizations: OrgInfo[];
  activeOrganizationId: number | null;
  role: 'admin' | 'editor' | 'viewer' | null;
  scopes: string[];
  features: string[];
  moduleAccess: ModuleAccess[];
  usesLegacyAccessFallback: boolean;
  license: LicenseState | null;
  publicSiteAccess: PublicSiteAccess | null;
  claims: TokenClaims | null;
  setTenant: (
    orgs: OrgInfo[],
    activeOrgId: number | null,
    role: OrgInfo['role'] | null,
    scopes: string[],
    features?: string[],
    license?: TenantState['license'],
    publicSiteAccess?: TenantState['publicSiteAccess'],
    moduleAccess?: ModuleAccess[],
    usesLegacyAccessFallback?: boolean,
  ) => void;
  setActiveOrg: (orgId: number) => void;
  setClaims: (claims: TokenClaims | null) => void;
};

export const useTenantStore = create<TenantState>((set, get) => ({
  organizations: [],
  activeOrganizationId: null,
  role: null,
  scopes: [],
  features: [],
  moduleAccess: [],
  usesLegacyAccessFallback: false,
  license: null,
  publicSiteAccess: null,
  claims: null,
  setTenant: (
    orgs,
    activeOrgId,
    role,
    scopes,
    features,
    license,
    publicSiteAccess,
    moduleAccess,
    usesLegacyAccessFallback,
  ) => {
    const organizations = (Array.isArray(orgs) ? orgs : [])
      .map((org) => ({
        ...org,
        id: typeof org.id === 'number' ? org.id : Number(org.id),
      }))
      .filter((org) => Number.isFinite(org.id));
    const storedOrgId = getStoredActiveOrgId();
    const claimsOrgId = get().claims?.org_id ?? null;
    const parsedActiveOrgId =
      typeof activeOrgId === 'number'
        ? activeOrgId
        : typeof activeOrgId === 'string'
          ? Number(activeOrgId)
          : NaN;
    const parsedClaimsOrgId =
      typeof claimsOrgId === 'number'
        ? claimsOrgId
        : typeof claimsOrgId === 'string'
          ? Number(claimsOrgId)
          : NaN;
    const parsedStoredOrgId =
      typeof storedOrgId === 'number'
        ? storedOrgId
        : typeof storedOrgId === 'string'
          ? Number(storedOrgId)
          : NaN;
    const candidateActiveOrgId = Number.isFinite(parsedActiveOrgId)
      ? parsedActiveOrgId
      : Number.isFinite(parsedStoredOrgId)
        ? parsedStoredOrgId
      : Number.isFinite(parsedClaimsOrgId)
        ? parsedClaimsOrgId
        : (organizations[0]?.id ?? null);
    const activeOrg =
      organizations.find((org) => org.id === candidateActiveOrgId) ?? organizations[0] ?? null;
    const resolvedActiveOrgId = activeOrg?.id ?? null;
    const resolvedRole = role ?? activeOrg?.role ?? null;

    set({
      organizations,
      activeOrganizationId: resolvedActiveOrgId,
      role: resolvedRole,
      scopes: Array.isArray(scopes) ? scopes : [],
      features: Array.isArray(features) ? features : [],
      moduleAccess: Array.isArray(moduleAccess) ? moduleAccess : [],
      usesLegacyAccessFallback: Boolean(usesLegacyAccessFallback),
      license: license ?? null,
      publicSiteAccess: publicSiteAccess ?? null,
    });
    setStoredActiveOrgId(resolvedActiveOrgId);
  },
  setActiveOrg: (orgId) => {
    const { organizations } = get();
    const org = organizations.find((o) => o.id === orgId);
    if (org) {
      set({ activeOrganizationId: orgId, role: org.role });
      setStoredActiveOrgId(orgId);
    }
  },
  setClaims: (claims) => set({ claims }),
}));
